import React, { useState, useEffect, useCallback } from 'react';
import oktaApiClient from '../okta/frontend/oktaApiClient';
import SpeedTestGroupForm from '../components/SpeedTestGroupForm';
import AddDevicesToGroupModal from '../components/AddDevicesToGroupModal';

const getSpeedColor = (val, thresholdLow = 4, thresholdHigh = 7) => {
  if (val == null || val === 0) return 'text-gray-400';
  if (val >= thresholdHigh) return 'text-green-600 font-semibold';
  if (val >= thresholdLow) return 'text-yellow-600 font-semibold';
  return 'text-red-600 font-semibold';
};

function GroupRow({ group, members, loadingMembers, onExpand, isExpanded, onEdit, onDelete, onToggle, onTrigger, onClearAndTrigger, onAddDevices, onRemoveDevices, onRefreshMembers, triggerResult, triggering }) {
  const isAutomated = group.group_type === 'automated';
  const isActive = Number(group.is_active) === 1;

  const avgDownload = members.length
    ? (members.reduce((s, m) => s + (Number(m.download_mbps) || 0), 0) / members.filter(m => m.download_mbps).length || null)
    : null;
  const avgUpload = members.length
    ? (members.reduce((s, m) => s + (Number(m.upload_mbps) || 0), 0) / members.filter(m => m.upload_mbps).length || null)
    : null;
  const avgPing = members.length
    ? (members.reduce((s, m) => s + (Number(m.ping_ms) || 0), 0) / members.filter(m => m.ping_ms).length || null)
    : null;

  return (
    <React.Fragment>
      <tr
        className="border-b hover:bg-gray-50 transition-colors cursor-pointer"
        onClick={onExpand}
      >
        {/* Status */}
        <td className="p-3 w-32">
          {isAutomated ? (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
              isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
            }`}>
              <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
              {isActive ? 'Active' : 'Inactive'}
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              manual
            </span>
          )}
        </td>

        {/* Name */}
        <td className="p-3">
          <div className="font-semibold text-gray-900">{group.group_name}</div>
          {group.description && (
            <div className="text-xs text-gray-500 mt-0.5">{group.description}</div>
          )}
        </td>

        {/* Devices */}
        <td className="p-3 text-center">
          <span className="text-sm font-medium">{Number(group.device_count)}</span>
          <span className="text-xs text-gray-400"> / 30</span>
        </td>

        {/* Avg Download */}
        <td className={`p-3 text-center text-sm ${getSpeedColor(avgDownload)}`}>
          {avgDownload ? `${avgDownload.toFixed(1)} Mbps` : <span className="text-gray-300">--</span>}
        </td>

        {/* Avg Upload */}
        <td className={`p-3 text-center text-sm ${getSpeedColor(avgUpload)}`}>
          {avgUpload ? `${avgUpload.toFixed(1)} Mbps` : <span className="text-gray-300">--</span>}
        </td>

        {/* Avg Ping */}
        <td className="p-3 text-center text-sm text-gray-700">
          {avgPing ? `${avgPing.toFixed(0)} ms` : <span className="text-gray-300">--</span>}
        </td>

        {/* Action */}
        <td className="p-3 text-right" onClick={e => e.stopPropagation()}>
          {isAutomated ? (
            <button
              onClick={() => onToggle(group.group_id, !isActive)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                isActive
                  ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {isActive ? '⏸ Stop' : '▶ Start'}
            </button>
          ) : (
            <button
              onClick={() => onTrigger(group.group_id)}
              disabled={triggering}
              className="px-3 py-1.5 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {triggering ? 'Running...' : '▶ Run Test'}
            </button>
          )}
        </td>

        {/* Expand chevron */}
        <td className="p-3 w-8 text-gray-400 text-center">
          {isExpanded ? '▲' : '▼'}
        </td>
      </tr>

      {/* Trigger result toast row */}
      {triggerResult && (
        <tr className="bg-blue-50 border-b border-blue-100">
          <td colSpan="8" className="px-4 py-2 text-sm text-blue-800">
            {triggerResult.error ? (
              <span className="flex items-center gap-3 flex-wrap">
                <span className="text-red-700">Error: {triggerResult.error}</span>
                {triggerResult.canClearStale && (
                  <button
                    onClick={() => onClearAndTrigger(triggerResult.groupId)}
                    disabled={triggering}
                    className="px-2 py-0.5 bg-yellow-100 text-yellow-800 border border-yellow-300 rounded text-xs font-medium hover:bg-yellow-200 transition-colors disabled:opacity-50"
                  >
                    🧹 Clear stale tests &amp; retry
                  </button>
                )}
              </span>
            ) : (
              <>
                Speed test triggered for <strong>{triggerResult.triggered}</strong> device(s)
                {triggerResult.skipped > 0 && (
                  <span className="ml-2 text-gray-500">({triggerResult.skipped} skipped)</span>
                )}
              </>
            )}
          </td>
        </tr>
      )}

      {/* Expanded detail */}
      {isExpanded && (
        <tr className="border-b bg-gray-50">
          <td colSpan="8" className="px-6 py-4">
            <ExpandedGroupDetail
              group={group}
              members={members}
              loading={loadingMembers}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggle={onToggle}
              onTrigger={onTrigger}
              onClearAndTrigger={onClearAndTrigger}
              onAddDevices={onAddDevices}
              onRemoveDevices={onRemoveDevices}
              onRefreshMembers={onRefreshMembers}
              triggering={triggering}
            />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

function ExpandedGroupDetail({ group, members, loading, onEdit, onDelete, onToggle, onTrigger, onClearAndTrigger, onAddDevices, onRemoveDevices, onRefreshMembers, triggering }) {
  const [selected, setSelected] = useState(new Set());
  const isAutomated = group.group_type === 'automated';
  const isActive = Number(group.is_active) === 1;

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRemove = () => {
    if (!selected.size) return;
    if (!window.confirm(`Remove ${selected.size} device(s) from this group?`)) return;
    onRemoveDevices(group.group_id, Array.from(selected));
    setSelected(new Set());
  };

  return (
    <div>
      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          onClick={() => onAddDevices(group)}
          className="px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
        >
          + Add Devices
        </button>
        <button
          onClick={() => onRefreshMembers(group.group_id)}
          disabled={loading}
          className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
          title="Refresh speed test results"
        >
          {loading ? 'Refreshing...' : '↻ Refresh Results'}
        </button>
        {selected.size > 0 && (
          <button
            onClick={handleRemove}
            className="px-3 py-1.5 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200 transition-colors"
          >
            Remove Selected ({selected.size})
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => onEdit(group)}
            className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 transition-colors"
          >
            Edit
          </button>
          <button
            onClick={() => {
              if (window.confirm(`Delete group "${group.group_name}"?`)) onDelete(group.group_id);
            }}
            className="px-3 py-1.5 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200 transition-colors"
          >
            Delete
          </button>
          {isAutomated ? (
            <button
              onClick={() => onToggle(group.group_id, !isActive)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                isActive
                  ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {isActive ? '⏸ Stop Schedule' : '▶ Start Schedule'}
            </button>
          ) : (
            <button
              onClick={() => onTrigger(group.group_id)}
              disabled={triggering}
              className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {triggering ? 'Running...' : '▶ Run Test Now'}
            </button>
          )}
        </div>
      </div>

      {isAutomated && (
        <p className="text-xs text-gray-500 mb-3">
          {isActive ? 'Runs automatically every hour.' : 'Schedule is stopped. Click "Start Schedule" to resume.'}
        </p>
      )}

      {/* Device table */}
      {loading ? (
        <p className="text-sm text-gray-500">Loading devices...</p>
      ) : members.length === 0 ? (
        <p className="text-sm text-gray-500">No devices in this group yet. Click "Add Devices" to add some.</p>
      ) : (
        <div className="overflow-x-auto rounded border border-gray-200">
          <table className="w-full text-sm bg-white">
            <thead className="bg-gray-100 text-gray-600">
              <tr>
                <th className="p-2 w-8"></th>
                <th className="p-2 text-left">Device ID</th>
                <th className="p-2 text-left">Yard</th>
                <th className="p-2 text-center">Status</th>
                <th className="p-2 text-center">Download</th>
                <th className="p-2 text-center">Upload</th>
                <th className="p-2 text-center">Ping</th>
                <th className="p-2 text-left">Last Test</th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.android_id} className={`border-t hover:bg-gray-50 ${selected.has(m.android_id) ? 'bg-blue-50' : ''}`}>
                  <td className="p-2 text-center">
                    <input
                      type="checkbox"
                      checked={selected.has(m.android_id)}
                      onChange={() => toggleSelect(m.android_id)}
                      onClick={e => e.stopPropagation()}
                    />
                  </td>
                  <td className="p-2 font-mono text-xs text-gray-700">
                    {m.friendly_name && <div className="font-sans font-medium text-gray-900 text-xs mb-0.5">{m.friendly_name}</div>}
                    {m.android_id}
                  </td>
                  <td className="p-2 text-gray-600">{m.yard_name || '--'}</td>
                  <td className="p-2 text-center">
                    {m.is_online != null ? (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${m.is_online ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
                        {m.is_online ? 'Online' : 'Offline'}
                      </span>
                    ) : <span className="text-gray-300">--</span>}
                  </td>
                  <td className={`p-2 text-center ${getSpeedColor(m.download_mbps)}`}>
                    {m.download_mbps ? `${Number(m.download_mbps).toFixed(1)}` : '--'}
                  </td>
                  <td className={`p-2 text-center ${getSpeedColor(m.upload_mbps)}`}>
                    {m.upload_mbps ? `${Number(m.upload_mbps).toFixed(1)}` : '--'}
                  </td>
                  <td className="p-2 text-center text-gray-600">
                    {m.ping_ms ? `${Number(m.ping_ms).toFixed(0)} ms` : '--'}
                  </td>
                  <td className="p-2 text-xs text-gray-400">
                    {m.last_test_at
                      ? new Date(m.last_test_at).toLocaleString()
                      : (m.added_at ? new Date(m.added_at).toLocaleDateString() : '--')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function SpeedTestGroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [membersCache, setMembersCache] = useState({}); // { groupId: [] }
  const [loadingMembers, setLoadingMembers] = useState({}); // { groupId: bool }
  const [triggerResults, setTriggerResults] = useState({}); // { groupId: result }
  const [triggering, setTriggering] = useState({}); // { groupId: bool }

  // Modal state
  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [addDevicesTarget, setAddDevicesTarget] = useState(null); // group object

  const fetchGroups = useCallback(async () => {
    try {
      const res = await oktaApiClient.get('/speed-test-groups');
      setGroups(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError('Failed to load groups.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const fetchMembers = useCallback(async (groupId, force = false) => {
    if (!force && membersCache[groupId]) return;
    setLoadingMembers(prev => ({ ...prev, [groupId]: true }));
    try {
      const res = await oktaApiClient.get(`/speed-test-groups/${groupId}/devices`);
      setMembersCache(prev => ({ ...prev, [groupId]: Array.isArray(res.data) ? res.data : [] }));
    } catch {
      setMembersCache(prev => ({ ...prev, [groupId]: [] }));
    } finally {
      setLoadingMembers(prev => ({ ...prev, [groupId]: false }));
    }
  }, [membersCache]);

  const handleExpand = (groupId) => {
    if (expandedId === groupId) {
      setExpandedId(null);
    } else {
      setExpandedId(groupId);
      fetchMembers(groupId);
    }
  };

  const handleTrigger = async (groupId) => {
    setTriggering(prev => ({ ...prev, [groupId]: true }));
    setTriggerResults(prev => ({ ...prev, [groupId]: null }));
    try {
      const res = await oktaApiClient.post(`/speed-test-groups/${groupId}/trigger`);
      setTriggerResults(prev => ({ ...prev, [groupId]: res.data }));
    } catch (err) {
      const isConflict = err.response?.status === 409;
      const msg = err.response?.data?.error || 'Failed to trigger group test.';
      setTriggerResults(prev => ({
        ...prev,
        [groupId]: {
          error: isConflict
            ? 'A speed test is already in progress for one or more devices in this group.'
            : msg,
          // Signal the toast to render the "clear & retry" button on 409.
          canClearStale: isConflict,
          groupId,
        }
      }));
    } finally {
      setTriggering(prev => ({ ...prev, [groupId]: false }));
      setTimeout(() => setTriggerResults(prev => ({ ...prev, [groupId]: null })), 8000);
    }
  };

  // Called by the "Clear stale tests & retry" button in the 409 toast.
  // Expires orphaned in-flight commands for the group's devices, then re-triggers.
  const handleClearAndTrigger = async (groupId) => {
    setTriggering(prev => ({ ...prev, [groupId]: true }));
    setTriggerResults(prev => ({ ...prev, [groupId]: null }));
    try {
      await oktaApiClient.post(`/speed-test-groups/${groupId}/clear-stale`);
      const res = await oktaApiClient.post(`/speed-test-groups/${groupId}/trigger`);
      setTriggerResults(prev => ({ ...prev, [groupId]: res.data }));
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to clear stale tests and retry.';
      setTriggerResults(prev => ({ ...prev, [groupId]: { error: msg } }));
    } finally {
      setTriggering(prev => ({ ...prev, [groupId]: false }));
      setTimeout(() => setTriggerResults(prev => ({ ...prev, [groupId]: null })), 8000);
    }
  };

  const handleToggle = async (groupId, active) => {
    try {
      await oktaApiClient.put(`/speed-test-groups/${groupId}/toggle`, { active });
      fetchGroups();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update group.');
    }
  };

  const handleDelete = async (groupId) => {
    try {
      await oktaApiClient.delete(`/speed-test-groups/${groupId}`);
      setGroups(prev => prev.filter(g => g.group_id !== groupId));
      if (expandedId === groupId) setExpandedId(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete group.');
    }
  };

  const handleRemoveDevices = async (groupId, androidIds) => {
    try {
      await oktaApiClient.delete(`/speed-test-groups/${groupId}/devices`, {
        data: { androidIds },
      });
      setMembersCache(prev => ({
        ...prev,
        [groupId]: (prev[groupId] || []).filter(m => !androidIds.includes(m.android_id)),
      }));
      fetchGroups();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove devices.');
    }
  };

  const handleGroupSaved = () => {
    setGroupFormOpen(false);
    setEditingGroup(null);
    fetchGroups();
  };

  const handleDevicesAdded = (groupId, androidIds) => {
    setMembersCache(prev => {
      const existing = prev[groupId] || [];
      const newEntries = androidIds
        .filter(id => !existing.some(m => m.android_id === id))
        .map(id => ({ android_id: id, added_at: new Date().toISOString() }));
      return { ...prev, [groupId]: [...existing, ...newEntries] };
    });
    fetchGroups();
  };

  const filtered = groups.filter(g =>
    g.group_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Group Test</h1>
          <p className="text-sm text-gray-500 mt-1">Create device groups and run speed tests in bulk.</p>
        </div>
        <button
          onClick={() => { setEditingGroup(null); setGroupFormOpen(true); }}
          className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors"
        >
          + Add New Group
        </button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search groups..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-72 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Groups table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm bg-white">
        <table className="w-full border-collapse">
          <thead className="bg-blue-600 text-white text-sm">
            <tr>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Group Name</th>
              <th className="p-3 text-center">Devices</th>
              <th className="p-3 text-center">Avg Download</th>
              <th className="p-3 text-center">Avg Upload</th>
              <th className="p-3 text-center">Avg Ping</th>
              <th className="p-3 text-right">Action</th>
              <th className="p-3 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="p-8 text-center text-gray-500">Loading groups...</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-8 text-center text-gray-500">
                  {search ? 'No groups match your search.' : 'No groups yet. Click "+ Add New Group" to create one.'}
                </td>
              </tr>
            ) : (
              filtered.map(group => (
                <GroupRow
                  key={group.group_id}
                  group={group}
                  members={membersCache[group.group_id] || []}
                  loadingMembers={loadingMembers[group.group_id]}
                  isExpanded={expandedId === group.group_id}
                  onExpand={() => handleExpand(group.group_id)}
                  onEdit={g => { setEditingGroup(g); setGroupFormOpen(true); }}
                  onDelete={handleDelete}
                  onToggle={handleToggle}
                  onTrigger={handleTrigger}
                  onClearAndTrigger={handleClearAndTrigger}
                  onAddDevices={g => setAddDevicesTarget(g)}
                  onRemoveDevices={handleRemoveDevices}
                  onRefreshMembers={id => fetchMembers(id, true)}
                  triggerResult={triggerResults[group.group_id]}
                  triggering={triggering[group.group_id]}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit group modal */}
      {groupFormOpen && (
        <SpeedTestGroupForm
          group={editingGroup}
          onClose={() => { setGroupFormOpen(false); setEditingGroup(null); }}
          onSaved={handleGroupSaved}
        />
      )}

      {/* Add devices modal */}
      {addDevicesTarget && (
        <AddDevicesToGroupModal
          group={addDevicesTarget}
          existingMembers={membersCache[addDevicesTarget.group_id] || []}
          onClose={() => setAddDevicesTarget(null)}
          onAdded={(androidIds) => {
            handleDevicesAdded(addDevicesTarget.group_id, androidIds);
            setAddDevicesTarget(null);
          }}
        />
      )}
    </div>
  );
}
